import { DateRange } from '../../types/dateRange';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Moment } from 'moment';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import DateRangePicker from '../../components/DateRangePicker';

import { BarChartProps } from './types';

const StyledBarChart = ({
  data,
  label,
  barDataKey,
  xDataKey,
  isWaste,
  value,
  setValue
}: BarChartProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const handleChangeRange = useCallback(
    (nextValue: DateRange<Moment>) => {
      setValue(nextValue);
    },
    [setValue]
  );

  return (
    <Paper
      sx={{
        padding: '20px',
        border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
        borderRadius: 2,
        minHeight: 560
      }}
    >
      <Stack spacing={2}>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
          }}
        >
          {label}
        </Typography>
        
        <DateRangePicker
          startText={t('stats.from')}
          endText={t('stats.to')}
          value={value}
          onChange={handleChangeRange}
        />

        {isWaste && (
          <Typography 
            sx={{ 
              fontSize: 12, 
              color: theme.palette.mode === 'light' ? '#D32F2F' : '#EF5350',
              fontStyle: 'italic'
            }}
          >
            {t('stats.countFromYesterday')}
          </Typography>
        )}

        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart 
              data={data} 
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <defs>
                <linearGradient id='colorBar' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#1976D2' stopOpacity={0.8}/>
                  <stop offset='95%' stopColor='#1976D2' stopOpacity={0.3}/>
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray='3 3' 
                stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}
              />
              <XAxis 
                dataKey={xDataKey} 
                angle={-45}
                textAnchor='end'
                height={80}
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 12
                }}
              />
              <YAxis 
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 12
                }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                  border: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}`,
                  borderRadius: '8px',
                  color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
                }}
              />
              <Bar 
                dataKey={barDataKey} 
                fill='url(#colorBar)'
                radius={[8, 8, 0, 0]}
                maxBarSize={60}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Paper>
  );
};

export default StyledBarChart;
